import { useEffect, useSyncExternalStore } from 'react';

export type Route =
  | { page: 'today' }
  | { page: 'summary' }
  | { page: 'roadmap' }
  | { page: 'projects' }
  | { page: 'interview' }
  | { page: 'guides' }
  | { page: 'settings' }
  | { page: 'day'; dayId: string }
  | { page: 'week'; weekId: string }
  | { page: 'project'; weekId: string }
  | { page: 'guide'; documentId: string }
  | { page: 'fixture'; filename: string };

function hashSnapshot(): string {
  return window.location.hash || '#/today';
}

function serverSnapshot(): string {
  return '#/today';
}

function subscribe(listener: () => void): () => void {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  const [page = 'today', rawId] = path.split('/');
  const id = rawId ? decodeURIComponent(rawId) : '';

  switch (page) {
    case 'roadmap':
    case 'summary':
    case 'projects':
    case 'interview':
    case 'guides':
    case 'settings':
      return { page };
    case 'day':
      return id ? { page, dayId: id } : { page: 'today' };
    case 'week':
    case 'project':
      return id ? { page, weekId: id } : { page: 'roadmap' };
    case 'guide':
      return id ? { page, documentId: id } : { page: 'guides' };
    case 'fixture':
      return id ? { page, filename: id } : { page: 'today' };
    default:
      return { page: 'today' };
  }
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, hashSnapshot, serverSnapshot);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [hash]);
  return parseHash(hash);
}

export function routeHref(route: Route): string {
  switch (route.page) {
    case 'today':
    case 'summary':
    case 'roadmap':
    case 'projects':
    case 'interview':
    case 'guides':
    case 'settings':
      return `#/${route.page}`;
    case 'day':
      return `#/day/${encodeURIComponent(route.dayId)}`;
    case 'week':
    case 'project':
      return `#/${route.page}/${encodeURIComponent(route.weekId)}`;
    case 'guide':
      return `#/guide/${encodeURIComponent(route.documentId)}`;
    case 'fixture':
      return `#/fixture/${encodeURIComponent(route.filename)}`;
  }
}

export function navigate(route: Route): void {
  const href = routeHref(route);
  if (window.location.hash === href) {
    window.dispatchEvent(new HashChangeEvent('hashchange'));
    return;
  }
  window.location.hash = href;
}
